<template>
    <div>
        <!--Button title-->
        <v-text-field
            v-model="cfg.buttonTitle"
            label="Button Title"
            placeholder="Generate Beeswax Report"
            single-line
            hide-details
            class="pa-0 ma-0"
            @input="emitChange"
        ></v-text-field>

        <!--Report type-->
        <v-select
            :items="reportOptions"
            v-model="cfg.reportType"
            label="Report"
            item-text="label"
            item-value="key"
            single-line
            hide-details
            class="pa-0 ma-0 mt-2"
            @change="onReportTypeChange"
        ></v-select>

        <!--Input mappings (dynamic per matterType)-->
        <div class="pt-2" v-if="cfg.reportType">
            <div class="caption greyType--text pb-1">
                Map each Beeswax input to a workflow field.
            </div>
            <table style="width: 100%; border-collapse: collapse">
                <tr v-for="input in inputSlots" :key="input.key">
                    <td style="width: 140px; vertical-align: middle" class="py-1 pr-2">
                        <span class="body-2">{{ input.label }}</span>
                        <span v-if="input.required" class="error--text">&nbsp;*</span>
                    </td>
                    <td style="vertical-align: middle" class="py-1">
                        <app-workflow-field-selector
                            :value="cfg.inputMappings[input.key]"
                            :fields="fields || []"
                            :steps="steps || []"
                            :field-type-filter="[1]"
                            @input="onMappingChange(input.key, $event)"
                        />
                    </td>
                </tr>
            </table>
        </div>

        <!--Screening checks-->
        <div class="pt-2" v-if="cfg.reportType">
            <div class="caption greyType--text pb-1">
                Screening checks to run when the report is generated. With none
                selected, only the matter is created — the report comes back unscreened.
            </div>
            <div style="display: flex; flex-wrap: wrap">
                <div
                    v-for="check in screeningOptions"
                    :key="check.key"
                    style="width: 50%; min-width: 180px"
                >
                    <v-checkbox
                        :label="check.label"
                        :input-value="cfg.screeningChecklist[check.key] === true"
                        hide-details
                        class="pa-0 ma-0 mb-1"
                        @change="onCheckToggle(check.key, $event)"
                    ></v-checkbox>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import WorkflowFieldSelector from "@/Admin/WorkflowEditor/Components/WorkflowManager/Workflow/Components/WorkflowFieldSelector.vue";

// Mirrors the REPORT_TYPES / MATTER_INPUTS constants in the backend
// (server/routes/api/beeswax/beeswax.js). Keep in sync when adding endpoints.
const REPORTS = [
    {key: 'cdd-report-natural-person',         matterType: 'naturalPerson', label: 'CDD Report — Natural Person'},
    {key: 'cdd-summary-report-natural-person', matterType: 'naturalPerson', label: 'CDD Summary — Natural Person'},
    {key: 'idv-report-natural-person',         matterType: 'naturalPerson', label: 'IDV Report — Natural Person'},
    {key: 'cdd-report-legal-person',           matterType: 'legalPerson',   label: 'CDD Report — Legal Person'},
];

const MATTER_INPUTS = {
    naturalPerson: [
        {key: 'firstName',      label: 'First Name',      required: true},
        {key: 'surname',        label: 'Surname',         required: true},
        {key: 'identityNumber', label: 'ID Number',       required: false},
        {key: 'passport',       label: 'Passport',        required: false},
    ],
    legalPerson: [
        {key: 'companyName',        label: 'Company Name',        required: true},
        {key: 'tradingName',        label: 'Trading Name',        required: false},
        {key: 'registrationNumber', label: 'Registration Number', required: true},
    ],
};

// Mirrors SCREENING_CHECKS in the backend (beeswax.js) — the flags accepted by
// the Beeswax custom-screening endpoints. `def` marks the ones toggled on by
// default for a new field: the checks that appear on the CDD summary report.
// IDV / bureau checks default off since they carry per-run charges.
const SCREENING_CHECKS = [
    {key: 'repCheck',                label: 'Reputationally Exposed (REP)', def: true},
    {key: 'pepCheck',                label: 'Politically Exposed (PEP)',    def: true},
    {key: 'sanctionsCheck',          label: 'Sanctions',                    def: true},
    {key: 'gazetteCheck',            label: 'Government Gazette',           def: true},
    {key: 'assetForfeitureCheck',    label: 'Asset Forfeitures',            def: true},
    {key: 'landClaimsCheck',         label: 'Land Claims',                  def: true},
    {key: 'tendersCheck',            label: 'Tenders',                      def: true},
    {key: 'pocaCheck',               label: 'POCA',                         def: true},
    {key: 'safpsCheck',              label: 'SAFPS',                        def: true},
    {key: 'adverseMediaCheck',       label: 'Adverse Media',                def: false},
    {key: 'enforcementActionsCheck', label: 'Enforcement Actions',          def: false},
    {key: 'globalLeaksCheck',        label: 'Global Leaks',                 def: false},
    {key: 'cipcCheck',               label: 'CIPC',                         def: false},
    {key: 'mastersOfficeCheck',      label: 'Masters Office',               def: false},
    {key: 'bestKnownAddressCheck',   label: 'Best Known Address',           def: false},
    {key: 'idvPhotoCheck',           label: 'ID Verification (Photo)',      def: false},
    {key: 'idvNoPhotoCheck',         label: 'ID Verification (No Photo)',   def: false},
];

export default {
    components: {
        appWorkflowFieldSelector: WorkflowFieldSelector,
    },
    props: {
        userInfo: Object,
        field:    Object,
        fields:   Array,
        steps:    Array,
    },
    data() {
        return {
            reportOptions: REPORTS,
        };
    },
    computed: {
        // field.lookup is persisted by the parent — that's our canonical config bag.
        cfg() {
            if (!this.field.lookup || typeof this.field.lookup !== 'object') {
                this.$set(this.field, 'lookup', {buttonTitle: '', reportType: null, inputMappings: {}, screeningChecklist: this.defaultChecklist()});
            } else {
                if (!this.field.lookup.inputMappings) this.$set(this.field.lookup, 'inputMappings', {});
                if (typeof this.field.lookup.buttonTitle === 'undefined') this.$set(this.field.lookup, 'buttonTitle', '');
                if (typeof this.field.lookup.reportType === 'undefined')  this.$set(this.field.lookup, 'reportType', null);
                // Fields configured before screening existed get the defaults.
                if (!this.field.lookup.screeningChecklist) this.$set(this.field.lookup, 'screeningChecklist', this.defaultChecklist());
            }
            return this.field.lookup;
        },
        screeningOptions() {
            return SCREENING_CHECKS;
        },
        matterType() {
            const r = REPORTS.find(r => r.key === this.cfg.reportType);
            return r ? r.matterType : null;
        },
        inputSlots() {
            return this.matterType ? MATTER_INPUTS[this.matterType] : [];
        },
    },
    methods: {
        defaultChecklist() {
            const out = {};
            for (const c of SCREENING_CHECKS) out[c.key] = c.def;
            return out;
        },
        onCheckToggle(key, val) {
            this.$set(this.cfg.screeningChecklist, key, val === true);
            this.emitChange();
        },
        emitChange() {
            this.$emit('input');
        },
        onReportTypeChange() {
            // Wipe mappings that don't apply to the new matter type so we don't
            // ship stale fieldIds to the backend.
            const valid = new Set(this.inputSlots.map(s => s.key));
            const fresh = {};
            for (const k of Object.keys(this.cfg.inputMappings || {})) {
                if (valid.has(k)) fresh[k] = this.cfg.inputMappings[k];
            }
            this.$set(this.cfg, 'inputMappings', fresh);
            this.emitChange();
        },
        onMappingChange(key, fieldId) {
            this.$set(this.cfg.inputMappings, key, fieldId || null);
            this.emitChange();
        },
    },
};
</script>
